import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const ChefsLabContext = createContext({});

export const useChefsLab = () => useContext(ChefsLabContext);

// localStorage keys
const PREFERENCES_KEY = 'chefslab-preferences';
const REPORTED_KEY = 'chefslab-reported-listings';

// Default preferences (CardChase Recommended)
const DEFAULT_PREFERENCES = {
  include: {
    outliers: false,
    flagged: false,
    rejected: false,
    reported: false,
  },
  selectedTimePeriod: '3m',
};

export const ChefsLabProvider = ({ children }) => {
  // Selected card for demo mode
  const [selectedCard, setSelectedCard] = useState(null);

  // Research data for selected card
  const [researchData, setResearchData] = useState(null);

  // User preferences (persisted to localStorage)
  const [preferences, setPreferencesState] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(PREFERENCES_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Migrate old display/cost structure to new include structure
          if (parsed.display || parsed.cost) {
            const migrated = {
              include: {
                outliers: parsed.cost?.includeOutliers || parsed.display?.showOutliers || false,
                flagged: parsed.cost?.includeFlagged || parsed.display?.showFlagged || false,
                rejected: parsed.cost?.includeRejected || parsed.display?.showRejected || false,
                reported: parsed.cost?.includeReported || parsed.display?.showReported || false,
              },
              selectedTimePeriod: parsed.selectedTimePeriod || '3m',
            };
            localStorage.setItem(PREFERENCES_KEY, JSON.stringify(migrated));
            return migrated;
          }
          return { ...DEFAULT_PREFERENCES, ...parsed };
        }
      } catch (e) {
        console.error('Error loading preferences:', e);
      }
    }
    return DEFAULT_PREFERENCES;
  });

  // Reported listings (persisted to localStorage)
  const [reportedListings, setReportedListingsState] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(REPORTED_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error('Error loading reported listings:', e);
      }
    }
    return {};
  });

  // Persist preferences to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
    }
  }, [preferences]);

  // Persist reported listings to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(REPORTED_KEY, JSON.stringify(reportedListings));
    }
  }, [reportedListings]);

  // Update preferences
  const setPreferences = useCallback((newPrefs) => {
    setPreferencesState(prev => ({
      ...prev,
      ...newPrefs,
      include: { ...prev.include, ...newPrefs.include },
    }));
  }, []);

  // Update include preference
  const setIncludePreference = useCallback((key, value) => {
    setPreferencesState(prev => ({
      ...prev,
      include: { ...prev.include, [key]: value },
    }));
  }, []);

  // Set selected time period
  const setSelectedTimePeriod = useCallback((period) => {
    setPreferencesState(prev => ({
      ...prev,
      selectedTimePeriod: period,
    }));
  }, []);

  // Reset preferences to defaults
  const resetPreferences = useCallback(() => {
    setPreferencesState(DEFAULT_PREFERENCES);
  }, []);

  // Add reported listing
  const addReportedListing = useCallback((url, report) => {
    setReportedListingsState(prev => ({
      ...prev,
      [url]: {
        ...report,
        reportedAt: Date.now(),
      },
    }));
  }, []);

  // Remove reported listing
  const removeReportedListing = useCallback((url) => {
    setReportedListingsState(prev => {
      const newState = { ...prev };
      delete newState[url];
      return newState;
    });
  }, []);

  // Check if listing is reported
  const isListingReported = useCallback((url) => {
    return !!reportedListings[url];
  }, [reportedListings]);

  // Get report for listing
  const getListingReport = useCallback((url) => {
    return reportedListings[url] || null;
  }, [reportedListings]);

  // Multi-source raw pricing (Poketrace + TCGdex + Gemini)
  const [rawSources, setRawSources] = useState(null);
  const [rawSourcesLoading, setRawSourcesLoading] = useState(false);

  // Clear selected card (go back to card selector)
  const clearSelectedCard = useCallback(() => {
    setSelectedCard(null);
    setResearchData(null);
    setRawSources(null);
    setRawSourcesLoading(false);
  }, []);


  // Calculate average price respecting include preferences
  const calculateAverage = useCallback((listings) => {
    if (!listings || listings.length === 0) return null;

    let filtered = [...listings];

    // Apply include preferences (if not included, filter out)
    if (!preferences.include.outliers) {
      filtered = filtered.filter(l => !l.outlier);
    }
    if (!preferences.include.flagged) {
      filtered = filtered.filter(l => !l.flagged);
    }
    if (!preferences.include.rejected) {
      filtered = filtered.filter(l => l.match_score === undefined || l.match_score >= 70);
    }
    if (!preferences.include.reported) {
      filtered = filtered.filter(l => !reportedListings[l.url]);
    }

    if (filtered.length === 0) return null;

    const sum = filtered.reduce((acc, l) => acc + (l.price || 0), 0);
    return sum / filtered.length;
  }, [preferences.include, reportedListings]);

  // Get listings to display (same as calculate - all in or all out)
  const getDisplayListings = useCallback((listings) => {
    if (!listings || !Array.isArray(listings)) return [];

    let result = [...listings];

    // Apply include preferences (same logic as calculateAverage)
    if (!preferences.include.outliers) {
      result = result.filter(l => !l.outlier);
    }
    if (!preferences.include.flagged) {
      result = result.filter(l => !l.flagged);
    }
    if (!preferences.include.rejected) {
      result = result.filter(l => l.match_score === undefined || l.match_score >= 70);
    }
    if (!preferences.include.reported) {
      result = result.filter(l => !reportedListings[l.url]);
    }

    return result;
  }, [preferences.include, reportedListings]);

  const value = useMemo(() => ({
    // Card selection
    selectedCard,
    setSelectedCard,
    clearSelectedCard,

    // Research data
    researchData,
    setResearchData,

    // Multi-source raw pricing
    rawSources,
    setRawSources,
    rawSourcesLoading,
    setRawSourcesLoading,

    // Preferences
    preferences,
    setPreferences,
    setIncludePreference,
    setSelectedTimePeriod,
    resetPreferences,

    // Reported listings
    reportedListings,
    addReportedListing,
    removeReportedListing,
    isListingReported,
    getListingReport,

    // Filtering functions
    calculateAverage,
    getDisplayListings,

    // Constants
    DEFAULT_PREFERENCES,
  }), [
    selectedCard,
    researchData,
    rawSources,
    rawSourcesLoading,
    preferences,
    reportedListings,
    setPreferences,
    setIncludePreference,
    setSelectedTimePeriod,
    resetPreferences,
    addReportedListing,
    removeReportedListing,
    isListingReported,
    getListingReport,
    calculateAverage,
    getDisplayListings,
    clearSelectedCard,
  ]);

  return (
    <ChefsLabContext.Provider value={value}>
      {children}
    </ChefsLabContext.Provider>
  );
};
